import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Unlock, Download, Sparkles, ArrowRight } from "lucide-react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { startCheckout, triggerDownload, hasSheet, isPremium } from "@/lib/premium";

const SHEET_ART = {
  "hiragana-deluxe": { chars: "あいう", bg: "from-sakura/50 to-peach/30" },
  "katakana-deluxe": { chars: "アイウ", bg: "from-ramune/50 to-sky/30" },
  "kana-master": { chars: "あア", bg: "from-lavender/60 to-sakura/30" },
  "kanji-n5-deluxe": { chars: "日月", bg: "from-matcha/60 to-mint/30" },
  "kanji-n4-deluxe": { chars: "旅道", bg: "from-yuzu/60 to-butter/30" },
  "kanji-n3-deluxe": { chars: "夢愛", bg: "from-coral/40 to-peach/30" },
  "kanji-all-deluxe": { chars: "漢字", bg: "from-ink/80 to-ink-soft/60 text-white" },
};

function SheetCard({ sheet, lang, t, user, onBuy, busy }) {
  const [downloading, setDownloading] = useState(false);
  const unlocked = hasSheet(user, sheet.id);
  const art = SHEET_ART[sheet.id] || { chars: "字", bg: "from-white to-white" };

  const download = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/pdf/premium/${sheet.id}?lang=${lang}`, { credentials: "include" });
      if (!res.ok) throw new Error("locked");
      triggerDownload(await res.blob(), `${sheet.id}_${lang}.pdf`);
    } catch {
      toast.error(t("premiumLocked"));
    } finally { setDownloading(false); }
  };

  return (
    <div className="kawaii-card kawaii-card-hover p-5 flex flex-col gap-4 relative" data-testid={`premium-sheet-${sheet.id}`}>
      <div className={`relative aspect-[4/3] rounded-2xl bg-gradient-to-br ${art.bg} flex items-center justify-center overflow-hidden`}>
        <span className={`font-jp text-6xl ${art.bg.includes("text-white") ? "text-white" : "text-ink"} ${unlocked ? "" : "blur-[2px] opacity-70"}`}>{art.chars}</span>
        {!unlocked && <div className="absolute inset-0 backdrop-blur-[3px] bg-white/30" />}
        <span className={`absolute top-3 left-3 kawaii-chip ${unlocked ? "!bg-matcha" : "gold-badge"}`} data-testid={`premium-sheet-${sheet.id}-status`}>
          {unlocked ? <Unlock className="w-3.5 h-3.5" strokeWidth={2.5} /> : <Lock className="w-3.5 h-3.5" strokeWidth={2.5} />}
          {unlocked ? t("premiumUnlocked") : t("premiumLocked")}
        </span>
      </div>
      <div className="flex-1">
        <div className="font-display text-lg text-ink leading-tight">{sheet.title[lang] || sheet.title.en}</div>
        <div className="text-xs text-ink-soft mt-1 uppercase font-bold tracking-wider">{sheet.system}{sheet.level ? ` · ${sheet.level}` : ""} · Deluxe</div>
      </div>
      {unlocked ? (
        <button data-testid={`premium-download-${sheet.id}`} onClick={download} disabled={downloading} className="kawaii-btn kawaii-btn-ink kawaii-btn-sm self-start">
          <Download className="w-4 h-4" strokeWidth={2.5} /> {downloading ? t("generating") : `${t("download")} · ${lang.toUpperCase()}`}
        </button>
      ) : (
        <button data-testid={`premium-buy-${sheet.id}`} onClick={() => onBuy(sheet.id)} disabled={busy} className="kawaii-btn kawaii-btn-primary kawaii-btn-sm self-start">
          <Sparkles className="w-4 h-4" strokeWidth={2.5} /> {t("buySingle")} · €2,99
        </button>
      )}
    </div>
  );
}

export default function PremiumSheets({ pdfLang }) {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sheets, setSheets] = useState([]);
  const [busy, setBusy] = useState(false);
  const premium = isPremium(user);

  useEffect(() => { api.get("/pdf/premium").then(({ data }) => setSheets(data)); }, []);

  const buy = async (sheetId) => {
    if (!user || user === false) {
      toast(t("loginToBuy"));
      navigate("/login", { state: { from: "/pdf" } });
      return;
    }
    setBusy(true);
    try { await startCheckout("pdf_single", sheetId); }
    catch { toast.error("Checkout failed"); setBusy(false); }
  };

  return (
    <section className="mb-16" data-testid="premium-section">
      <div className="kawaii-card shimmer-border p-6 sm:p-8 mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="max-w-xl">
          <div className="kawaii-chip gold-badge mb-3"><Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} /> {t("premium")}</div>
          <h2 className="font-display text-2xl sm:text-3xl text-ink">{t("premiumSheets")}</h2>
          <p className="text-sm sm:text-base text-ink-soft mt-2">{premium ? t("youArePremium") : t("premiumSheetsSub")}</p>
        </div>
        {!premium && (
          <Link to="/pricing" data-testid="premium-view-plans-btn" className="kawaii-btn kawaii-btn-ink">
            {t("unlockAll")} <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
          </Link>
        )}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {sheets.map((s) => <SheetCard key={s.id} sheet={s} lang={pdfLang} t={t} user={user} onBuy={buy} busy={busy} />)}
      </div>
    </section>
  );
}
